import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { EquipmentFlagGate } from "@/components/equipment/EquipmentFlagGate";
import { EquipmentFilters } from "@/components/equipment/EquipmentFilters";
import { EquipmentTable } from "@/components/equipment/EquipmentTable";
import { EquipmentFormDialog } from "@/components/equipment/EquipmentFormDialog";
import { ALL_DEPARTMENTS } from "@/components/equipment/DepartmentToggle";
import { AvailabilityPeriodFilter } from "@/components/equipment/AvailabilityPeriodFilter";
import { useDepartments } from "@/hooks/use-departments";
import { useEquipmentAccess, useEquipmentCategories, useEquipmentList } from "@/hooks/use-equipment";
import { useEquipmentBookingLines, useEquipmentUsage } from "@/hooks/use-equipment-bookings";
import { bookingState, isFreeInPeriod, type DateRange } from "@/lib/equipment/availability";
import { isBookable } from "@/lib/equipment/booking-draft";
import type { DBEquipmentItem } from "@/lib/db-types";
import { filterEquipment } from "@/lib/equipment/filter";
import { insuranceState, todayIso } from "@/lib/equipment/insurance";

/**
 * /utstyr (VIB-2917, skjerm A): én tabell med kategorichips, avdelingsbryter
 * som starter på egen avdeling, søk og status. Ingen nøkkeltall.
 */
export default function EquipmentPage() {
  return (
    <EquipmentFlagGate>
      <EquipmentList />
    </EquipmentFlagGate>
  );
}

function EquipmentList() {
  const navigate = useNavigate();
  const access = useEquipmentAccess();
  const { data: departments = [] } = useDepartments();
  const { data: categories = [] } = useEquipmentCategories();
  const { data: items = [], isLoading, error } = useEquipmentList(access.canManage, !access.isLoading);
  const [department, setDepartment] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [period, setPeriod] = useState<DateRange | null>(null);
  const today = todayIso();
  const { data: bookingLines = [] } = useEquipmentBookingLines(period?.from ?? today);
  const { data: usage } = useEquipmentUsage();

  // Starter på egen avdeling; uten avdeling på brukeren vises begge.
  const departmentValue = department ?? access.departmentId ?? ALL_DEPARTMENTS;
  const categoryBookable = useMemo(() => new Map(categories.map((c) => [c.id, c.bookable])), [categories]);
  // «Ledig i periode» snevrer inn før avdeling, søk og kategori, så tallene på chipene stemmer.
  const candidates = useMemo(
    () => (period ? items.filter((i) => isBookable(i, categoryBookable) && isFreeInPeriod(i, bookingLines, period)) : items),
    [items, period, bookingLines, categoryBookable],
  );
  const { rows, countsByCategory, scopedTotal } = filterEquipment(candidates, {
    departmentId: departmentValue === ALL_DEPARTMENTS ? null : departmentValue,
    categoryId,
    search,
  });

  const categoryNames = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories]);
  const departmentNames = useMemo(() => new Map(departments.map((d) => [d.id, d.name])), [departments]);
  const expiringCount = useMemo(() => {
    return access.canManage ? items.filter((i) => insuranceState(i, today) === "expiring").length : 0;
  }, [items, access.canManage, today]);

  return (
    <div className="page-container animate-fade-in space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2">
          <SrIcon name="package" className="h-6 w-6" />
          Utstyr
        </h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to="/utstyr/belegg">
              <SrIcon name="calendar" className="h-4 w-4" />
              Belegg
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/utstyr/service">
              <SrIcon name="wrench" className="h-4 w-4" />
              Service
            </Link>
          </Button>
          {access.canManage && (
            <>
              <Button variant="outline" asChild>
                <Link to="/utstyr/forsikring">
                  <SrIcon name="clipboard-check" className="h-4 w-4" />
                  Forsikring
                  {expiringCount > 0 && (
                    <span className="ml-1 rounded-full bg-amber-100 px-2 text-xs text-amber-800">{expiringCount} utløper</span>
                  )}
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/utstyr/produktdata">
                  <SrIcon name="database" className="h-4 w-4" />
                  Produktdata
                </Link>
              </Button>
              <Button onClick={() => setFormOpen(true)}>
                <SrIcon name="plus" className="h-4 w-4" />
                Registrer utstyr
              </Button>
            </>
          )}
        </div>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          <EquipmentFilters
            departments={departments}
            department={departmentValue}
            onDepartmentChange={setDepartment}
            search={search}
            onSearchChange={setSearch}
            categories={categories}
            categoryId={categoryId}
            onCategoryChange={setCategoryId}
            countsByCategory={countsByCategory}
            total={scopedTotal}
          />
          <AvailabilityPeriodFilter period={period} defaultFrom={today} onChange={setPeriod} />
          {error ? (
            <p className="text-destructive">Feil ved lasting av utstyr: {(error as Error).message}</p>
          ) : isLoading || access.isLoading ? (
            <div className="flex justify-center py-8">
              <SrIcon name="loader2" className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <EquipmentTable
                items={rows}
                categoryName={(id) => categoryNames.get(id) ?? "–"}
                departmentName={(id) => (id ? (departmentNames.get(id) ?? "–") : "Ukjent")}
                showAmounts={access.canManage}
                stateFor={(i: DBEquipmentItem) => bookingState(i, bookingLines, period, today)}
                usageFor={(id) => usage?.get(id)}
              />
              <p className="text-xs text-muted-foreground">
                Viser {rows.length} av {items.length} · sortert på navn
              </p>
            </>
          )}
        </CardContent>
      </Card>
      {access.canManage && (
        <EquipmentFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          item={null}
          categories={categories}
          departments={departments}
          defaultDepartmentId={departmentValue === ALL_DEPARTMENTS ? access.departmentId : departmentValue}
        />
      )}
    </div>
  );
}
